import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useMemo, useState } from 'react';

const StallingOffersVisual = ({ model, isRTL = false }) => {
  useUiLanguage();
  const dataset = model.dataset;
  const lanes = dataset?.lanes || [];
  const summary = dataset?.summary || {};
  const labels = summary.labels || {};
  const [activePageIndex, setActivePageIndex] = useState(0);
  const pages = useMemo(
    () => [
      {
        id: 'summary',
        title: summary.title || (isRTL ? 'ملخص التعثر' : 'Stalling summary')
      },
      ...lanes.map((lane) => ({
        id: lane.id,
        title: lane.label
      }))
    ],
    [isRTL, lanes, summary.title]
  );

  if (!lanes.length) return null;

  if ((model.size || 'medium') === 'compact') {
    return (
      <div className="analytics-stalling-compact">
        {lanes.map((lane) => (
          <div key={lane.id} className="analytics-stalling-compact-row">
            <span>{uiText(lane.label)}</span>
            <strong>{lane.segments.length}</strong>
          </div>
        ))}
      </div>
    );
  }

  const activePage = pages[activePageIndex] || pages[0];
  const activeLane = activePage.id === 'summary'
    ? null
    : lanes.find((lane) => lane.id === activePage.id) || lanes[0];

  const goPrev = () => {
    setActivePageIndex((prev) => (prev === 0 ? pages.length - 1 : prev - 1));
  };

  const goNext = () => {
    setActivePageIndex((prev) => (prev + 1) % pages.length);
  };

  return (
    <div className="analytics-stalling">
      <div className="analytics-stalling-summary">
        <div className="analytics-stalling-summary-card">
          <span>{labels.stalledOffers || uiText("Stalled offers")}</span>
          <strong>{summary.totalStalled}</strong>
        </div>
        <div className="analytics-stalling-summary-card danger">
          <span>{labels.pastSla || uiText("Past SLA")}</span>
          <strong>{summary.breached}</strong>
        </div>
        <div className="analytics-stalling-summary-card">
          <span>{labels.oldestWait || uiText("Oldest wait")}</span>
          <strong>{summary.oldest}{labels.daySuffix || 'd'}</strong>
        </div>
      </div>

      <div className="analytics-stalling-page">
        <div className="analytics-stalling-key" aria-label={uiText("Offer stalling legend")}>
          <span className="analytics-stalling-key-label">
            <span className="analytics-stalling-key-dot accent" />
            {labels.keySafe || uiText("Within SLA")}
          </span>
          <span className="analytics-stalling-key-label">
            <span className="analytics-stalling-key-dot warning" />
            {labels.keyWatch || uiText("Near threshold")}
          </span>
          <span className="analytics-stalling-key-label">
            <span className="analytics-stalling-key-dot danger" />
            {labels.keyUrgent || uiText("Past threshold")}
          </span>
        </div>

        {activePage.id === 'summary' ? (
          <div className="analytics-stalling-summary-page">
            {lanes.map((lane) => (
              <article key={lane.id} className="analytics-stalling-summary-lane">
                <div className="analytics-stalling-head">
                  <div className="analytics-stalling-title-block">
                    <div className="analytics-stalling-title-line">
                      <span className="analytics-visual-label">{uiText(lane.label)}</span>
                      <span className="analytics-stalling-info-wrap">
                        <button
                          type="button"
                          className="analytics-stalling-info-btn"
                          aria-label={lane.emphasis}
                        >
                          <i className="fa-solid fa-circle-info" />
                        </button>
                        <span className="analytics-stalling-tooltip">{lane.emphasis}</span>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="analytics-stalling-lane-stats">
                  <div className="analytics-stalling-lane-stat">
                    <span>{labels.stalledOffers || uiText("Stalled offers")}</span>
                    <strong>{lane.segments.length}</strong>
                  </div>
                  <div className="analytics-stalling-lane-stat danger">
                    <span>{labels.pastSla || uiText("Past SLA")}</span>
                    <strong>{lane.breachedCount}</strong>
                  </div>
                  <div className="analytics-stalling-lane-stat">
                    <span>{labels.oldestWait || uiText("Oldest wait")}</span>
                    <strong>{lane.oldestDays}{labels.daySuffix || 'd'}</strong>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div key={activeLane.id} className="analytics-stalling-row">
            <div className="analytics-stalling-head">
              <div className="analytics-stalling-title-block">
                <div className="analytics-stalling-title-line">
                  <span className="analytics-visual-label">{uiText(activeLane.label)}</span>
                  <span className="analytics-stalling-info-wrap">
                    <button
                      type="button"
                      className="analytics-stalling-info-btn"
                      aria-label={activeLane.emphasis}
                    >
                      <i className="fa-solid fa-circle-info" />
                    </button>
                    <span className="analytics-stalling-tooltip">{activeLane.emphasis}</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="analytics-stalling-lane-stats">
              <div className="analytics-stalling-lane-stat">
                <span>{labels.stalledOffers || uiText("Stalled offers")}</span>
                <strong>{activeLane.segments.length}</strong>
              </div>
              <div className="analytics-stalling-lane-stat danger">
                <span>{labels.pastSla || uiText("Past SLA")}</span>
                <strong>{activeLane.breachedCount}</strong>
              </div>
              <div className="analytics-stalling-lane-stat">
                <span>{labels.threshold || uiText("Threshold")}</span>
                <strong>{activeLane.thresholdDays}{labels.thresholdSuffix || 'd'} {labels.slaSuffix || uiText("SLA")}</strong>
              </div>
            </div>

            <div className="analytics-stalling-track">
              {activeLane.segments.map((segment) => (
                <button
                  key={segment.id}
                  type="button"
                  className={`analytics-stalling-segment ${segment.tone || 'accent'}`}
                  title={`${segment.roleLabel} · ${segment.daysOpen}d`}
                >
                  <strong>{segment.roleLabel}</strong>
                  <span>{segment.meta}</span>
                </button>
              ))}
            </div>

            <div className="analytics-stalling-footer">
              <span>{labels.oldestInQueue || uiText("Oldest in queue")}: {activeLane.oldestDays}{labels.daySuffix || 'd'}</span>
              <span>{labels.threshold || uiText("Threshold")}: {activeLane.thresholdDays}{labels.thresholdSuffix || 'd'}</span>
            </div>
          </div>
        )}

        <div className="analytics-stalling-pager">
          <button
            type="button"
            className="executive-overview-nav-btn"
            onClick={goPrev}
          >
            <i className={`fa-solid ${isRTL ? 'fa-circle-chevron-right' : 'fa-circle-chevron-left'}`} />
            {isRTL ? 'السابق' : 'Prev'}
          </button>
          <div className="analytics-stalling-page-indicator">{activePageIndex + 1}/{pages.length}</div>
          <button
            type="button"
            className="executive-overview-nav-btn"
            onClick={goNext}
          >
            {isRTL ? 'التالي' : 'Next'}
            <i className={`fa-solid ${isRTL ? 'fa-circle-chevron-left' : 'fa-circle-chevron-right'}`} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default StallingOffersVisual;
